import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from '../utils/toast';
import { jsPDF } from 'jspdf';
import { Plus, Trash2, Printer, Save, RefreshCw, Edit, ArrowLeft, ShoppingCart, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../config/api';
import { formatDisplayDate } from '../utils/dateFormat';
import { isValidPhoneNumber, normalizePhoneInput } from '../utils/formValidation';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeader,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanel,
  masterPanelPadding,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterSubtitle,
  masterTitle,
  masterTableData,
  masterValueBox
} from '../components/masterPageStyles';

const PAYMENT_MODES = ['Cash', 'UPI', 'Card', 'Other'];

const createEmptyItem = (index = 0) => ({
  lineId: `${Date.now()}-${index}`,
  medicine_id: '',
  stock_id: '',
  medicine_name: '',
  generic_id: '',
  generic_name: '',
  batch_number: '',
  expiry_date: '',
  qty: '',
  price: 0,
  discount_percent: '',
  discount: '',
  gst: 0
});

const formatMoney = (value) => Number(value || 0).toFixed(2);

const formatDateTimeForInvoice = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

const getDisplayBillId = (bill) => bill?.bill_id || bill?.billId || 'DRAFT-INVOICE';
const getBatchLookupKey = (medicineId, batchNumber) =>
  `${String(medicineId || '').trim()}::${String(batchNumber || '').trim().toUpperCase()}`;

const getLineValues = (item) => {
  const qty = Number(item.qty || 0);
  const price = Number(item.price || 0); 
  const gstRate = Number(item.gst || 0);
  const discPercent = Number(item.discount_percent || 0);

  const gross = qty * price;
  const discount = (gross * discPercent) / 100;
  const netValue = gross - discount;
  const taxable = (netValue * 100) / (100 + gstRate);
  const gstAmount = netValue - taxable;

  return {
    gross: Number(gross.toFixed(2)),
    discount: Number(discount.toFixed(2)),
    taxable: Number(taxable.toFixed(2)),
    gstAmount: Number(gstAmount.toFixed(2)),
    total: Number(netValue.toFixed(2)) 
  };
};

const resolveStoredGstPercent = (item, medicine) => {
  const rawPercent = Number(item?.gst_percent ?? item?.gst ?? 0);
  if (Number.isFinite(rawPercent) && rawPercent >= 0 && rawPercent <= 100) return rawPercent;
  return Number(medicine?.GST_percentage || medicine?.gst_per || 0);
};

const normalizeModeString = (value) => {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const emptyPaymentAmounts = () => ({
  Cash: '',
  UPI: '',
  Card: '',
  Other: ''
});

const PharmacyBilling = () => {
  const navigate = useNavigate();
  const [patientType, setPatientType] = useState('outside');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [patientName, setPatientName] = useState('');
  const [doctorSearch, setDoctorSearch] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [linkedPrescription, setLinkedPrescription] = useState('');
  const [useGenericFilter, setUseGenericFilter] = useState(false);
  const [billDiscountPercent, setBillDiscountPercent] = useState('');
  const [billDiscountAmount, setBillDiscountAmount] = useState('');
  const [paymentModes, setPaymentModes] = useState(['Cash']);
  const [paymentAmounts, setPaymentAmounts] = useState(emptyPaymentAmounts);
  const [items, setItems] = useState([createEmptyItem(0)]);
  const [medicines, setMedicines] = useState([]);
  const [grns, setGrns] = useState([]);
  const [stockList, setStockList] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [bills, setBills] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showForm, setShowForm] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingSnapshotItems, setEditingSnapshotItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [submitAction, setSubmitAction] = useState('submit');

  const [prescriptionSelectionModal, setPrescriptionSelectionModal] = useState(null);

  const loadData = async () => {
    try {
      const [medRes, grnRes, stockRes, rxRes, billRes, patientRes, doctorRes] = await Promise.all([
        api.get('/master/medicine/all'),
        api.get('/grn'),
        api.get('/stock'),
        api.get('/prescriptions/all'),
        api.get('/pharmacy/all'),
        api.get('/patients/all'),
        api.get('/doctors/all')
      ]);
      setMedicines(Array.isArray(medRes.data?.data) ? medRes.data.data : Array.isArray(medRes.data) ? medRes.data : []);
      if (Array.isArray(grnRes.data)) setGrns(grnRes.data || []);
      if (Array.isArray(stockRes.data)) setStockList(stockRes.data || []);
      setPrescriptions(Array.isArray(rxRes.data?.data) ? rxRes.data.data : Array.isArray(rxRes.data) ? rxRes.data : []);
      setBills(Array.isArray(billRes.data?.data) ? billRes.data.data : Array.isArray(billRes.data) ? billRes.data : []);
      setPatients(Array.isArray(patientRes.data?.data) ? patientRes.data.data : Array.isArray(patientRes.data) ? patientRes.data : []);
      setDoctors(Array.isArray(doctorRes.data?.data) ? doctorRes.data.data : Array.isArray(doctorRes.data) ? doctorRes.data : []);
    } catch (err) {
      toast.error('Failed to load pharmacy data');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const medicineMap = useMemo(() => {
    const map = new Map();
    medicines.forEach((med) => {
      const keys = [med?.medicine_id, med?.medicine_code, med?._id].filter(Boolean);
      keys.forEach((key) => map.set(String(key), med));
    });
    return map;
  }, [medicines]);

  const doctorMap = useMemo(() => {
    const map = new Map();
    doctors.forEach((doc) => {
      const keys = [doc?.doctorId, doc?._id].filter(Boolean);
      keys.forEach((key) => map.set(String(key), doc));
    });
    return map;
  }, [doctors]);

  const stockSummary = useMemo(() => {
    const summary = {};
    stockList.forEach((stock) => {
      const rawId = stock?.medicine_id ? String(stock.medicine_id) : '';
      const resolvedMedicine = medicineMap.get(rawId);
      const id = resolvedMedicine?.medicine_id !== undefined ? String(resolvedMedicine.medicine_id) : rawId;
      if (!id) return;
      const qty = Number((stock.total_grn_received_quantity ?? stock.total_stock_available) || 0);
      if (!summary[id]) {
        summary[id] = {
          qty: 0,
          earliestExpiry: null,
          defaultPrice: 0
        };
      }
      summary[id].qty += qty;

      (stock.batches || []).forEach((batch) => {
        if (Number(batch?.mrp || 0) > 0 && summary[id].defaultPrice === 0) {
          summary[id].defaultPrice = Number(batch.mrp || 0);
        }
        if (batch?.expiry_date) {
          const expiryDate = new Date(batch.expiry_date);
          if (!summary[id].earliestExpiry || expiryDate < summary[id].earliestExpiry) {
            summary[id].earliestExpiry = expiryDate;
            if (Number(batch?.mrp || 0) > 0) {
              summary[id].defaultPrice = Number(batch.mrp || 0);
            }
          }
        }
      });
    });
    return summary;
  }, [stockList, medicineMap]);

  const batchMap = useMemo(() => {
    const map = new Map();
    stockList.forEach((stock) => {
      const medicineId = String(stock?.medicine_id || '').trim();
      const medicine = medicineMap.get(medicineId);
      if (!medicineId) return;

      (stock.batches || []).forEach((batch) => {
        const batchNumber = String(batch?.batch_number || '').trim().toUpperCase();
        if (!batchNumber) return;

        const key = getBatchLookupKey(medicineId, batchNumber);
        map.set(key, {
          stock_id: String(batch?.stock_id || '').trim(),
          batch_number: String(batch?.batch_number || '').trim(),
          medicine_id: medicineId,
          generic_id: String(medicine?.genericId || medicine?.generic_id || ''),
          quantity_available: Number((batch?.total_grn_received_quantity ?? batch?.quantity_available) || 0),
          mrp: Number(batch?.mrp || medicine?.MRP || 0),
          per_medicine_price: Number(batch?.per_medicine_price || batch?.mrp || 0),
          expiry_date: batch?.expiry_date || null
        });
      });
    });
    return map;
  }, [stockList, medicineMap]);

  const grnItemRows = useMemo(() => {
    return grns.flatMap((grn) =>
      Array.isArray(grn?.items)
        ? grn.items.map((item, index) => ({
            key: `${grn?._id || grn?.grn_number || 'grn'}-${item?._id || index}`,
            grn_number: grn?.grn_number || '',
            medicine_id: String(item?.medicine_id || ''),
            batch_number: String(item?.batch_number || '').trim(),
            expiry_date: item?.expiry_date || '',
            mrp: Number(item?.mrp || 0),
            conversion_factor: Number(item?.conversion_factor || 1),
            received_quantity: Number(item?.received_quantity || 0)
          }))
        : []
    );
  }, [grns]);

  const grnStockSummary = useMemo(() => {
    const summary = {};
    grnItemRows.forEach((item) => {
      const medicineId = String(item?.medicine_id || '');
      if (!medicineId) return;
      if (!summary[medicineId]) {
        summary[medicineId] = {
          qty: 0,
          defaultPrice: 0
        };
      }
      summary[medicineId].qty += Number(item?.received_quantity || 0) * Number(item?.conversion_factor || 1);
      if (Number(item?.mrp || 0) > 0 && summary[medicineId].defaultPrice === 0) {
        summary[medicineId].defaultPrice = Number(item.mrp || 0);
      }
    });
    return summary;
  }, [grnItemRows]);

  const grnBatchMap = useMemo(() => {
    const map = new Map();
    grnItemRows.forEach((item) => {
      const batchNumber = String(item?.batch_number || '').trim().toUpperCase();
      const medicineId = String(item?.medicine_id || '');
      if (!batchNumber || !medicineId) return;
      const key = getBatchLookupKey(medicineId, batchNumber);
      if (!map.has(key)) {
        map.set(key, {
          stock_id: '',
          batch_number: String(item.batch_number || '').trim(),
          medicine_id: medicineId,
          generic_id: '',
          quantity_available: 0,
          mrp: Number(item?.mrp || 0),
          expiry_date: item?.expiry_date || ''
        });
      }
      const current = map.get(key);
      const cf = Number(item?.conversion_factor || 1);
      current.quantity_available += Number(item?.received_quantity || 0) * cf;
    });
    return map;
  }, [grnItemRows]);

  const mergedStockSummary = useMemo(() => {
    const summary = { ...grnStockSummary };
    Object.entries(stockSummary).forEach(([medicineId, data]) => {
      summary[medicineId] = {
        qty: Number(data?.qty || 0),
        defaultPrice: Number(data?.defaultPrice || summary[medicineId]?.defaultPrice || 0)
      };
    });
    return summary;
  }, [grnStockSummary, stockSummary]);

  const stockByMedicine = useMemo(() => {
    const map = new Map();
    
    stockList.forEach((stock) => {
      const medicineId = String(stock.medicine_id);
      const medicine = medicineMap.get(medicineId);
      
      const formattedBatches = (stock.batches || []).map(b => ({
        stock_id: b.stock_id || '',
        batch_number: b.batch_number,
        quantity_available: Number((b.total_grn_received_quantity ?? b.quantity_available) || 0),
        mrp: Number(b.mrp || medicine?.MRP || 0),
        per_medicine_price: Number(b.per_medicine_price || b.mrp || 0),
        expiry_date: b.expiry_date || '',
        medicine_id: medicineId,
        generic_id: String(medicine?.genericId || medicine?.generic_id || ''),
        medicine_name: medicine?.medicine_name || '',
        generic_name: medicine?.generic_name || '',
        gst: Number(medicine?.GST_percentage || medicine?.gst_per || 0),
        grn_number: b.grn_number,
        po_number: b.po_number 
      }));

      formattedBatches.sort((a, b) => {
        const getExpiryValue = (dateStr) => {
          if (!dateStr || !dateStr.includes('/')) return 999999; 
          const [month, year] = dateStr.split('/').map(Number);
          return (year * 12) + month;
        };
        return getExpiryValue(a.expiry_date) - getExpiryValue(b.expiry_date); 
      });

      map.set(medicineId, formattedBatches);
    });
    
    return map;
  }, [stockList, medicineMap]);

  const availableMedicineOptions = useMemo(() => {
    return stockList
      .filter(stock => Number((stock.total_grn_received_quantity ?? stock.total_stock_available) || 0) > 0)
      .map((stock) => {
        const medicine = medicineMap.get(String(stock.medicine_id));
        return {
          value: stock.medicine_id,
          label: medicine?.medicine_name || stock.medicine_id,
          search: [
            medicine?.medicine_name,
            medicine?.medicine_code,
            medicine?.generic_name,
            stock.medicine_id
          ].filter(Boolean).join(' '),
          generic_name: medicine?.generic_name || '',
          qty: Number((stock.total_grn_received_quantity ?? stock.total_stock_available) || 0)
        };
      })
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [stockList, medicineMap]);

  const patientOptions = useMemo(() => {
    return patients
      .map((patient) => ({
        value: patient.patientId || patient._id,
        label: patient.fullName || patient.patientId || '',
        phone: patient.mobileNumber || '',
        search: `${patient.fullName} ${patient.patientId} ${patient.mobileNumber}`.toLowerCase(),
        patient
      }))
      .filter((item) => item.value && item.label)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [patients]);

  const selectedPatient = useMemo(() => {
    return patientOptions.find((item) => String(item.value) === String(selectedPatientId))?.patient || null;
  }, [patientOptions, selectedPatientId]);

  const patientDoctorOptions = useMemo(() => {
    if (!selectedPatient) {
      return doctors
        .map((doc) => ({
          value: doc.doctorId || doc._id,
          label: doc.doctorName || doc.doctorId || '',
          search: [doc.doctorName, doc.doctorId].filter(Boolean).join(' ')
        }))
        .filter((item) => item.value && item.label)
        .sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }

    const doctorBucket = new Map();
    const historyRows = [
      ...(Array.isArray(selectedPatient.visitHistory) ? selectedPatient.visitHistory : []),
      ...(Array.isArray(selectedPatient.billingHistory) ? selectedPatient.billingHistory : [])
    ];

    historyRows.forEach((row) => {
      const doctorId = row?.doctorId || '';
      const label = row?.doctorName || doctorMap.get(String(doctorId))?.doctorName || doctorId;
      if (!doctorId || !label) return;
      doctorBucket.set(String(doctorId), {
        value: String(doctorId),
        label,
        search: [label, doctorId, row?.department].filter(Boolean).join(' ')
      });
    });

    if (doctorBucket.size === 0) {
      doctors.forEach((doc) => {
        if (doc?.doctorId && doc?.doctorName) {
          doctorBucket.set(String(doc.doctorId), {
            value: String(doc.doctorId),
            label: doc.doctorName,
            search: [doc.doctorName, doc.doctorId].filter(Boolean).join(' ')
          });
        }
      });
    }

    return Array.from(doctorBucket.values()).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [selectedPatient, doctors, doctorMap]);

  const prescriptionOptions = useMemo(() => {
    return prescriptions.map((rx) => ({
      value: rx.prescriptionId || rx._id,
      label: `${rx.prescriptionId || rx._id} | ${rx.patientName || rx.patient || 'Patient'}`
    }));
  }, [prescriptions]);

  const editingReservedStock = useMemo(() => {
    const map = new Map();
    editingSnapshotItems.forEach((item) => {
      const medicineId = String(item?.medicine_id || '');
      if (!medicineId) return;
      map.set(medicineId, (map.get(medicineId) || 0) + Number(item.qty || 0));
    });
    return map;
  }, [editingSnapshotItems]);

  const editingReservedBatchStock = useMemo(() => {
    const map = new Map();
    editingSnapshotItems.forEach((item) => {
      const medicineId = String(item?.medicine_id || '').trim();
      const batchNumber = String(item?.batch_number || '').trim().toUpperCase();
      if (!medicineId || !batchNumber) return;
      const key = getBatchLookupKey(medicineId, batchNumber);
      map.set(key, (map.get(key) || 0) + Number(item.qty || 0));
    });
    return map;
  }, [editingSnapshotItems]);

  const getAvailableStock = (medicineId) => {
    const id = String(medicineId || '');
    if (!id) return 0;
    const liveStock = Number(mergedStockSummary[id]?.qty || 0);
    const reserved = editingId ? Number(editingReservedStock.get(id) || 0) : 0;
    return liveStock + reserved;
  };

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + Number(getLineValues(item).total || 0), 0);
    const totalGst = items.reduce((sum, item) => sum + Number(getLineValues(item).gstAmount || 0), 0);
    const totalLineDiscount = items.reduce((sum, item) => sum + Number(getLineValues(item).discount || 0), 0);
    const headerDiscount = Number(billDiscountAmount || 0); 
    const grandTotalRaw = Math.max(subtotal - headerDiscount, 0);

    return {
      subtotal: Number(subtotal.toFixed(2)),
      gstAmount: Number(totalGst.toFixed(2)),
      lineDiscount: Number(totalLineDiscount.toFixed(2)),
      total: Math.round(grandTotalRaw) 
    };
  }, [items, billDiscountAmount]);

  const paymentEnteredTotal = useMemo(() => {
    return paymentModes.reduce((sum, mode) => sum + Number(paymentAmounts[mode] || 0), 0);
  }, [paymentAmounts, paymentModes]);

  const rebalancePaymentAmounts = (modes, draftAmounts, totalAmount) => {
    const nextAmounts = { ...emptyPaymentAmounts(), ...draftAmounts };
    const activeModes = modes.filter(Boolean);

    if (activeModes.length === 0) {
      nextAmounts.Cash = totalAmount ? formatMoney(totalAmount) : '';
      return nextAmounts;
    }

    if (activeModes.length === 1) {
      const onlyMode = activeModes[0];
      nextAmounts[onlyMode] = totalAmount ? formatMoney(totalAmount) : '';
      return nextAmounts;
    }

    const autoMode = activeModes[activeModes.length - 1];
    const manualTotal = activeModes
      .filter((mode) => mode !== autoMode)
      .reduce((sum, mode) => sum + Number(nextAmounts[mode] || 0), 0);

    const remaining = Math.max(Number(totalAmount || 0) - manualTotal, 0);
    nextAmounts[autoMode] = formatMoney(remaining);
    return nextAmounts;
  };

  const filteredBills = useMemo(() => {
    const sortedList = [...bills].sort((a, b) => {
      const idA = String(a.bill_id || a.billId || a._id || '');
      const idB = String(b.bill_id || b.billId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((bill) => {
      const billId = String(bill?.bill_id || bill?.billId || '').toLowerCase();
      const patientName = String(bill?.patient_name || '').toLowerCase();
      const doctorName = String(bill?.doctor_name || '').toLowerCase();
      const phoneNumber = String(bill?.phone_number || '').toLowerCase();

      return (
        billId.startsWith(q) ||
        patientName.startsWith(q) ||
        doctorName.startsWith(q) ||
        phoneNumber.startsWith(q)
      );
    });
  }, [bills, searchTerm]);

  const paginatedBills = useMemo(() => {
    if (pageSize === -1) return filteredBills;
    const start = (currentPage - 1) * pageSize;
    return filteredBills.slice(start, start + pageSize);
  }, [filteredBills, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filteredBills.length, pageSize]);

  useEffect(() => {
    if (patientType !== 'inside') return;
    if (!selectedPatient) {
      setPatientName('');
      setPhoneNumber('');
      setDoctorName('');
      setDoctorSearch('');
      return;
    }
    setPatientName(selectedPatient.fullName || '');
    setPhoneNumber(selectedPatient.mobileNumber || '');
    setPatientSearch(selectedPatient.fullName || '');
  }, [patientType, selectedPatient]);

  useEffect(() => {
    setPaymentAmounts((prev) => rebalancePaymentAmounts(paymentModes, prev, totals.total));
  }, [paymentModes, totals.total]);

  const getMedicineIdByName = (name) => {
    const normalized = String(name).trim().toLowerCase();
    const match = medicines.find(m => String(m.medicine_name).trim().toLowerCase() === normalized);
    return match ? match.medicine_id : null;
  };

const autofillMedicinesList = (prescriptionItems) => {
    if (!Array.isArray(prescriptionItems) || prescriptionItems.length === 0) return;
    
    const preparedLines = prescriptionItems.map((med, idx) => {
      // 1. ATTEMPT ID LOOKUP FIRST (The ID-First Requirement)
      // If med.medicine_id exists in prescription, use it. Else, fallback to name-based lookup.
      const idFromPrescription = med.medicine_id; 
      
      const targetMatch = idFromPrescription 
        ? medicineMap.get(String(idFromPrescription)) 
        : medicines.find(m => String(m.medicine_name).trim().toLowerCase() === String(med.name).trim().toLowerCase());

      const verifiedMedicineId = targetMatch ? String(targetMatch.medicine_id) : '';

      if (verifiedMedicineId) {
        const batchesList = stockByMedicine.get(verifiedMedicineId) || [];
        const validLiveBatch = batchesList.find(b => Number(b.quantity_available || 0) > 0);
        const resolvedMedicine = medicineMap.get(verifiedMedicineId);

        return {
          lineId: `${Date.now()}-${idx}-${Math.random()}`,
          medicine_id: verifiedMedicineId,
          stock_id: validLiveBatch ? String(validLiveBatch.stock_id) : '',
          medicine_name: String(resolvedMedicine?.medicine_name || med.name).toUpperCase(),
          generic_id: String(resolvedMedicine?.genericId || resolvedMedicine?.generic_id || ''),
          generic_name: String(resolvedMedicine?.generic_name || '').toUpperCase(),
          batch_number: validLiveBatch ? validLiveBatch.batch_number : '',
          expiry_date: validLiveBatch ? validLiveBatch.expiry_date : '',
          qty: med.duration ? parseFloat(med.duration) || 1 : 1,
          price: validLiveBatch ? Number(validLiveBatch.per_medicine_price || 0) : 0,
          discount_percent: '',
          discount: '',
          gst: resolvedMedicine ? Number(resolvedMedicine.GST_percentage || resolvedMedicine.gst_per || 0) : 0
        };
      }

      // Fallback for when no ID can be resolved
      return {
        lineId: `${Date.now()}-${idx}-${Math.random()}`,
        medicine_id: '',
        stock_id: '',
        medicine_name: String(med.name || '').toUpperCase(),
        generic_id: '',
        generic_name: '',
        batch_number: '',
        expiry_date: '',
        qty: med.duration ? parseFloat(med.duration) || 1 : 1,
        price: 0,
        discount_percent: '',
        discount: '',
        gst: 0
      };
    }).filter(Boolean);

    setItems(preparedLines.length ? preparedLines : [createEmptyItem(0)]);
  };

  const openHandwrittenWindow = (filesArray) => {
    if (!Array.isArray(filesArray) || filesArray.length === 0) return;
    filesArray.forEach((file) => {
      if (file.data) {
        const win = window.open('', '_blank');
        if (win) {
          if (String(file.data).startsWith('data:application/pdf')) {
            win.document.write(`<iframe src="${file.data}" style="width:100%; height:100%; border:none;"></iframe>`);
          } else {
            win.document.write(`<img src="${file.data}" style="max-width:100%; height:auto;" />`);
          }
          win.document.close();
        }
      }
    });
  };

  const handleItemChange = (lineId, field, value) => {
    setItems((prev) =>
      prev.map((item) => (item.lineId === lineId ? { ...item, [field]: value } : item))
    );
  };

  const handleLineDiscountPercentChange = (lineId, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.lineId !== lineId) return item;
        const gross = Number(item.qty || 0) * Number(item.price || 0);
        const discountAmount = value === '' ? '' : ((gross * Number(value || 0)) / 100).toFixed(2);
        return { ...item, discount_percent: value, discount: discountAmount };
      })
    );
  };

  const handleLineDiscountAmountChange = (lineId, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.lineId !== lineId) return item;
        const gross = Number(item.qty || 0) * Number(item.price || 0);
        const pct = value === '' || gross <= 0 ? '' : ((Number(value || 0) / gross) * 100).toFixed(2);
        return { ...item, discount: value, discount_percent: pct };
      })
    );
  };

  const handlePaymentAmountChange = (mode, value) => {
    setPaymentAmounts((prev) => rebalancePaymentAmounts(paymentModes, { ...prev, [mode]: value }, totals.total));
  };

  const selectMedicineForLine = (lineId, medicineId) => {
    const medicine = medicineMap.get(String(medicineId));
    setItems((prev) =>
      prev.map((item) => {
        if (item.lineId !== lineId) return item;
        return {
          ...item,
          medicine_id: String(medicineId),
          stock_id: '',
          medicine_name: String(medicine?.medicine_name || '').toUpperCase(),
          generic_id: String(medicine?.genericId || medicine?.generic_id || ''),
          generic_name: String(medicine?.generic_name || '').toUpperCase(),
          batch_number: '',
          expiry_date: '',
          price: 0,
          gst: Number(medicine?.GST_percentage || medicine?.gst_per || 0)
        };
      })
    );
  };

  const selectBatchForLine = (lineId, batchValue) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.lineId !== lineId) return item;
        const lookupKey = getBatchLookupKey(item.medicine_id, batchValue);
        const batchInfo = batchMap.get(lookupKey) || grnBatchMap.get(lookupKey);
        if (!batchInfo) return item;
        const medicine = medicineMap.get(String(batchInfo.medicine_id));
        const unitPrice = Number(batchInfo.per_medicine_price || 0);
        return {
          ...item,
          medicine_id: String(batchInfo.medicine_id),
          stock_id: String(batchInfo.stock_id || item.stock_id || ''),
          medicine_name: String(medicine?.medicine_name || item.medicine_name || '').toUpperCase(),
          generic_id: String(batchInfo.generic_id || medicine?.genericId || medicine?.generic_id || item.generic_id || ''),
          generic_name: String(medicine?.generic_name || item.generic_name || '').toUpperCase(),
          batch_number: batchInfo.batch_number,
          expiry_date: batchInfo.expiry_date || '',
          price: Number(batchInfo.per_medicine_price || 0),
          gst: Number(medicine?.GST_percentage || medicine?.gst_per || 0)
          
        };
      })
    );
  };

  const getMedicineOptionsForItem = (item) => {
    const genericQuery = String(item.generic_name || '').trim().toLowerCase();
    if (!useGenericFilter || !genericQuery) return availableMedicineOptions;
    return availableMedicineOptions.filter((option) =>
      String(option.generic_name || '').toLowerCase().includes(genericQuery)
    );
  };

  const addItem = () => setItems((prev) => [...prev, createEmptyItem(prev.length)]);

  const removeItem = (lineId) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((item) => item.lineId !== lineId) : prev));
  };

  const resetForm = () => {
    setPatientType('outside');
    setSelectedPatientId('');
    setPatientSearch('');
    setPatientName('');
    setDoctorSearch('');
    setDoctorName('');
    setPhoneNumber('');
    setLinkedPrescription('');
    setUseGenericFilter(false);
    setBillDiscountPercent('');
    setBillDiscountAmount('');
    setPaymentModes(['Cash']);
    setPaymentAmounts(emptyPaymentAmounts());
    setItems([createEmptyItem(0)]);
    setEditingId(null);
    setEditingSnapshotItems([]);
    setPrescriptionSelectionModal(null);
  };

  const hasStockIssues = useMemo(() => {
    return items.some((item) => {
      if (!item.medicine_id && !item.batch_number) return false;
      const batchNumber = String(item.batch_number || '').trim().toUpperCase();
      if (batchNumber) {
        const batchKey = getBatchLookupKey(item.medicine_id, batchNumber);
        const batchInfo = batchMap.get(batchKey) || grnBatchMap.get(batchKey);
        const liveStock = Number(batchInfo?.quantity_available || 0);
        const reserved = editingId ? Number(editingReservedBatchStock.get(batchKey) || 0) : 0;
        return Number(item.qty || 0) > liveStock + reserved;
      }
      const id = String(item.medicine_id || '');
      const liveStock = Number(mergedStockSummary[id]?.qty || 0);
      const reserved = editingId ? Number(editingReservedStock.get(id) || 0) : 0;
      return Number(item.qty || 0) > liveStock + reserved;
    });
  }, [items, editingId, editingReservedBatchStock, editingReservedStock, mergedStockSummary, batchMap, grnBatchMap]);

  const buildBillPdf = (bill) => {
    const data = bill || {};
    const itemsList = data.items || [];
    const doc = new jsPDF('p', 'mm', 'a4');
    
    const primaryGreen = [0, 128, 0];
    const darkText = [55, 65, 81];
    const left = 8;
    const right = 202;
    let y = 12; 
    const topBoxY = 8;
    const boxWidth = right - left;

    const safeStr = (val) => String(val || '').toUpperCase();
    const safeNum = (val) => Number(val || 0).toFixed(2);

    doc.setDrawColor(primaryGreen[0], primaryGreen[1], primaryGreen[2]);
    doc.setLineWidth(0.5);
    doc.rect(5, 5, 200, 287);

    doc.setFontSize(8);
    doc.setTextColor(darkText[0], darkText[1], darkText[2]);

    const colL_Lab = 10;   
    const colL_Val = 26;   
    const colR_Lab = 145;  
    const colR_Val = 162;  

    doc.setFont('helvetica', 'bold').text('Bill No.', colL_Lab, y);
    doc.setFont('helvetica', 'normal').text(`: ${getDisplayBillId(data)}`, colL_Val, y);
    doc.setFont('helvetica', 'bold').text('Branch', colR_Lab, y);
    doc.setFont('helvetica', 'normal').text(`: Dumdum`, colR_Val, y);

    y += 4.2;
    doc.setFont('helvetica', 'bold').text('D & T', colL_Lab, y);
    doc.setFont('helvetica', 'normal').text(`: ${formatDateTimeForInvoice(data.createdAt || new Date())}`, colL_Val, y);
    doc.setFont('helvetica', 'bold').text('Tel No.', colR_Lab, y);
    doc.setFont('helvetica', 'normal').text(`: 03325294005/06`, colR_Val, y);

    const logoY = 10;
    doc.setLineWidth(0.3);
    doc.setDrawColor(primaryGreen[0], primaryGreen[1], primaryGreen[2]);
    doc.setTextColor(primaryGreen[0], primaryGreen[1], primaryGreen[2]);
    doc.setFont('helvetica', 'bold').setFontSize(14).text('ASHA The Hope', 83, logoY + 6);
    doc.setFontSize(6.5).setTextColor(darkText[0], darkText[1], darkText[2]);

    y += 4.2;
    doc.setFontSize(8).setTextColor(darkText[0], darkText[1], darkText[2]);
    doc.setFont('helvetica', 'bold').text('Name', colL_Lab, y);
    doc.setFont('helvetica', 'normal').text(`: ${safeStr(data.patient_name || 'Walk-in Customer')}`, colL_Val, y);
    doc.setFont('helvetica', 'bold').text('GSTIN', colR_Lab, y);
    doc.setFont('helvetica', 'normal').text(`: 09AACCA7574P1ZD`, colR_Val, y);

    y += 4.2;
    doc.setFont('helvetica', 'bold').text('Dr. Name', colL_Lab, y);
    doc.setFont('helvetica', 'normal').text(`: ${safeStr(data.doctor_name || 'N/A')}`, colL_Val, y);
    doc.setFont('helvetica', 'bold').text('FSSAI', colR_Lab, y);
    doc.setFont('helvetica', 'normal').text(`: 12820120000326`, colR_Val, y);

    y += 4.2;
    doc.setFont('helvetica', 'bold').text('Mobile', colL_Lab, y);
    doc.setFont('helvetica', 'normal').text(`: ${safeStr(data.phone_number || 'N/A')}`, colL_Val, y);
    doc.setFont('helvetica', 'bold').text('DL No.', colR_Lab, y);
    doc.setFontSize(6).text(`: UP/AD/BIO/R/688128-HB`, colR_Val, y);

    const topBoxHeight = (y + 1.5) - topBoxY;
    doc.setLineWidth(0.35);
    doc.rect(left, topBoxY, boxWidth, topBoxHeight);

    y += 5;
    doc.setTextColor(primaryGreen[0], primaryGreen[1], primaryGreen[2]);
    doc.setFont('helvetica', 'bold').setFontSize(10).text('INVOICE', 92, y);

    y += 1;
    const tableBoxY = y;
    doc.setFillColor(primaryGreen[0], primaryGreen[1], primaryGreen[2]);
    doc.rect(left, y, 194, 6, 'F');
    doc.setTextColor(255).setFontSize(7);

    const col = {
      name: 9,
      qty: 49,
      hsn: 58,
      mfrs: 75,
      batch: 92,
      exp: 108,
      sch: 125,
      mrp: 135,
      cgst: 148,
      sgst: 161,
      tax: 184,    
      tot: 201     
    };

    doc.text('ITEM NAME', col.name, y + 4);
    doc.text('QTY', col.qty, y + 4);
    doc.text('HSN', col.hsn, y + 4);
    doc.text('MFRS', col.mfrs, y + 4);
    doc.text('BATCH', col.batch, y + 4);
    doc.text('EXP', col.exp, y + 4);
    doc.text('SCH', col.sch, y + 4);
    doc.text('MRP', col.mrp, y + 4);
    doc.text('CGST', col.cgst, y + 4);
    doc.text('SGST', col.sgst, y + 4);
    doc.text('TAX', col.tax, y + 4, { align: 'right' });
    doc.text('TOTAL', col.tot, y + 4, { align: 'right' });

    y += 9;
    doc.setTextColor(darkText[0], darkText[1], darkText[2]).setFont('helvetica', 'normal').setFontSize(6.5);
    const rowHeight = 3.5;

    itemsList.forEach((item) => {
      const med = typeof medicineMap !== 'undefined' ? medicineMap.get(String(item.medicine_id)) : {};
      const lineTotal = Number(item.total || 0);
      const taxableVal = Number(item.taxable_value || 0);
      const gstRate = Number(item.gst_percent || 0);
      const halfGst = (gstRate / 2).toFixed(2);

      doc.text(safeStr(item.medicine_name).substring(0, 22), col.name, y);
      doc.text(safeStr(item.qty), col.qty, y);
      doc.text(safeStr(med?.hsn_code || '30049099'), col.hsn, y);
      doc.text(safeStr(med?.manufacturer || 'GEN').substring(0, 8), col.mfrs, y);
      doc.text(safeStr(item.batch_number || '-'), col.batch, y);
      doc.text(safeStr(item.expiry_date || '--/--'), col.exp, y);
      doc.text(safeStr(med?.schedule || 'H'), col.sch, y);
      doc.text(safeNum(item.per_medicine_mrp), col.mrp, y);
      
      doc.text(`${halfGst}%`, col.cgst, y);
      doc.text(`${halfGst}%`, col.sgst, y);
      
      doc.text(safeNum(taxableVal), col.tax, y, { align: 'right' });
      doc.text(safeNum(lineTotal), col.tot, y, { align: 'right' });
      
      y += rowHeight;
    });

    const tableBoxHeight = Math.max(12, y - tableBoxY);
    doc.setLineWidth(0.35);
    doc.rect(left, tableBoxY, boxWidth, tableBoxHeight);

    y += 2;
    const summaryX = 156;
    const summaryY = y;
    const summaryWidth = 46;
    const summaryHeight = 16; 
      
    doc.setFont('helvetica', 'bold').setFontSize(8).setTextColor(darkText[0], darkText[1], darkText[2]);
    doc.setLineWidth(0.35);
    doc.rect(summaryX, summaryY, summaryWidth, summaryHeight);
      
    doc.text('TOTAL', summaryX + 3, summaryY + 4);
    doc.text(safeNum(data.subtotal), summaryX + summaryWidth - 3, summaryY + 4, { align: 'right' });
      
    doc.text('DISCOUNT', summaryX + 3, summaryY + 8);
    doc.text(safeNum(data.discount), summaryX + summaryWidth - 3, summaryY + 8, { align: 'right' });
      
    doc.setFontSize(10).setTextColor(primaryGreen[0], primaryGreen[1], primaryGreen[2]);
    doc.text('NET TOTAL', summaryX + 3, summaryY + 13);
    doc.text(safeNum(data.total_amount), summaryX + summaryWidth - 3, summaryY + 13, { align: 'right' });
    return doc;
  };

  const printBillPdf = (bill) => {
    const doc = buildBillPdf(bill);
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    const printWindow = window.open(blobUrl, '_blank');
    if (printWindow) printWindow.focus();
  };

  const handleEdit = (bill) => {
    setEditingId(bill._id);
    setPatientType(bill.patient_type || 'outside');
    setPatientName(bill.patient_name || '');
    setPatientSearch(bill.patient_name || '');
    setDoctorName(bill.doctor_name || '');
    setDoctorSearch(bill.doctor_name || '');
    setPhoneNumber(bill.phone_number || '');
    setLinkedPrescription(bill.prescription_id || '');

    const existingPatient = patients.find((patient) =>
      String(patient.fullName || '').trim().toLowerCase() === String(bill.patient_name || '').trim().toLowerCase() ||
      String(patient.mobileNumber || '').trim() === String(bill.phone_number || '').trim()
    );
    setSelectedPatientId(existingPatient?.patientId || existingPatient?._id || '');

    const modes = normalizeModeString(bill.payment_mode);
    setPaymentModes(modes.length ? modes : ['Cash']);

    const nextPaymentAmounts = emptyPaymentAmounts();
    if (Array.isArray(bill.payment_breakdown)) {
      bill.payment_breakdown.forEach((entry) => {
        if (entry?.mode && Object.prototype.hasOwnProperty.call(nextPaymentAmounts, entry.mode)) {
          nextPaymentAmounts[entry.mode] = entry.amount ? formatMoney(entry.amount) : '';
        }
      });
    } else if ((modes.length || 1) === 1) {
      nextPaymentAmounts[modes[0] || 'Cash'] = bill.total_amount ? formatMoney(bill.total_amount) : '';
    }
    setPaymentAmounts(rebalancePaymentAmounts(modes.length ? modes : ['Cash'], nextPaymentAmounts, Number(bill.total_amount || 0)));

    const billItems = (bill.items || []).map((item, index) => {
      const medicine = medicineMap.get(String(item.medicine_id));
      return {
        lineId: `${bill._id}-${index}`,
        medicine_id: item.medicine_id,
        stock_id: item.stock_id || '',
        medicine_name: (item.medicine_name || medicine?.medicine_name || '').toUpperCase(),
        generic_id: item.generic_id || medicine?.genericId || medicine?.generic_id || item.generic_id || '',
        generic_name: (medicine?.generic_name || '').toUpperCase(),
        batch_number: item.batch_number || '',
        expiry_date: item.expiry_date || '',
        qty: item.qty,
        price: item.per_medicine_mrp  || 0,
        discount_percent: item.discount_percent || '',
        discount: item.discount || '',
        gst: resolveStoredGstPercent(item, medicine)
      };
    });

    setBillDiscountPercent(bill.discount ? bill.discount.toString() : '');
    const headerDiscountValue = Math.max((bill.subtotal || 0) - (bill.total_amount || 0), 0);
    setBillDiscountAmount(headerDiscountValue > 0 ? headerDiscountValue.toFixed(2) : '');
      
    setItems(billItems.length ? billItems : [createEmptyItem(0)]);
    setEditingSnapshotItems(billItems);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOrderMedicine = (item) => {
    const medicine = medicineMap.get(String(item?.medicine_id || ''));
    if (!medicine) {
      toast.error('Select a medicine first');
      return;
    }

    const availableStock = getAvailableStock(item.medicine_id);
    const requestedQty = Number(item?.qty || 0);
    const defaultQty = Number(medicine.reorder_level || 0) || requestedQty || 1;
    const suggestedQty = Math.max(defaultQty - availableStock, requestedQty, 1);

    navigate('/PurchaseOrder', {
      state: {
        prefillItems: [
          {
            medicine_id: String(medicine.medicine_id || item.medicine_id || ''),
            ordered_quantity: suggestedQty,
            purchase_price: Number(medicine.purchase_price || 0)
          }
        ]
      }
    });
  };

  const handleDelete = async (bill) => {
    if (!window.confirm(`Delete bill ${bill.bill_id}?`)) return;
    try {
      const res = await api.delete(`/pharmacy/${bill._id}`);
      if (res.data?.success) {
        toast.success('Bill deleted');
        if (Array.isArray(res.data.warnings) && res.data.warnings.length > 0) {
          toast.error(res.data.warnings[0]);
        }
        await loadData();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Delete failed';
      toast.error(msg);
    }
  };

  const togglePaymentMode = (mode) => {
    setPaymentModes((prev) => {
      if (prev.includes(mode)) {
        if (prev.length === 1) return prev;
        return prev.filter((entry) => entry !== mode);
      }
      return [...prev, mode];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!patientName.trim()) return toast.error('Patient name is required');
    if (!doctorName.trim()) return toast.error('Doctor name is required');
    if (!phoneNumber.trim()) return toast.error('Phone number is required');
    if (!isValidPhoneNumber(phoneNumber)) return toast.error('Phone number must be exactly 10 digits');

    const validItems = items.filter((item) => item.medicine_id && Number(item.qty || 0) > 0);
    if (validItems.length === 0) return toast.error('Add at least one medicine');

    const preparedPaymentBreakdown = paymentModes.map((mode) => ({
      mode,
      amount: Number(Number(paymentAmounts[mode] || 0).toFixed(2)) 
    }));

    const enteredAmount = preparedPaymentBreakdown.reduce((sum, item) => sum + item.amount, 0);

    if (paymentModes.length > 1 && Math.abs(enteredAmount - totals.total) > 0.01) {
      return toast.error(`Payment amount (${enteredAmount.toFixed(2)}) must match total (${totals.total.toFixed(2)})`);
    }

    const finalPaymentBreakdown = paymentModes.length === 1
      ? [{ mode: paymentModes[0], amount: totals.total }]
      : preparedPaymentBreakdown;

    setIsSubmitting(true);

    try {
      const payload = {
        patient_type: patientType,
        patient_name: patientName.trim().toUpperCase(),
        doctor_name: doctorName.trim().toUpperCase(),
        phone_number: phoneNumber.trim(),
        prescription_id: linkedPrescription || null,
        subtotal: totals.subtotal, 
        gst_amount: totals.gstAmount,
        discount: Number(billDiscountPercent || 0), 
        overall_discount_amount: Number(billDiscountAmount || 0),
        total_line_discount: totals.lineDiscount,
        total_amount: totals.total, 
        payment_mode: paymentModes.join(', '),
        payment_breakdown: finalPaymentBreakdown,
        items: validItems.map((item) => {
          const line = getLineValues(item);
          return {
            medicine_id: item.medicine_id,
            stock_id: item.stock_id || '',
            generic_id: item.generic_id || '',
            medicine_name: item.medicine_name,
            batch_number: item.batch_number.trim().toUpperCase(),
            expiry_date: item.expiry_date || null,
            qty: Number(item.qty || 0),
            per_medicine_mrp: Number(Number(item.price).toFixed(2)),
            discount_percent: Number(item.discount_percent || 0),
            discount: Number(line.discount.toFixed(2)),
            gst_percent: Number(item.gst || 0),
            taxable_value: Number(line.taxable.toFixed(2)),
            gst_amount: Number(line.gstAmount.toFixed(2)),
            per_medicine_price: Number(item.per_medicine_price || item.price),
            total: Number(line.total.toFixed(2)) 
          };
        })
      };

      const res = editingId
        ? await api.put(`/pharmacy/update/${editingId}`, payload)
        : await api.post('/pharmacy/generate-bill', payload);

      if (res.data?.success) {
        toast.success(editingId ? 'Bill updated successfully' : 'Bill generated successfully');
        if (submitAction === 'print') {
          printBillPdf(res.data.data);
        }
        resetForm();
        setShowForm(true);
        await loadData(); 
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Billing failed';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={masterPageShell}>
      <header className={masterHeader}>
        <div className={`${masterHeaderCard} max-w-6xl`}>
          <div className="flex items-center gap-1">
            <div className={masterHeaderIcon}>
              <Printer size={10} />
            </div>
            <div>
              <h1 className={masterTitle}>Pharmacy Billing</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!showForm ? (
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className={masterAccentButton}
              >
                <Plus size={14} /> Add Billing
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-slate-700">
                    <input
                      type="radio"
                      checked={patientType === 'inside'}
                      onChange={() => setPatientType('inside')}
                    />
                    Inside Patient
                  </label>
                  <label className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-slate-700">
                    <input
                      type="radio"
                      checked={patientType === 'outside'}
                      onChange={() => setPatientType('outside')}
                    />
                    Outside Patient
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className={masterPrimaryButton}
                  >
                    <ArrowLeft size={10} />VIEW LIST
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className={masterPrimaryButton}
                  >
                    <RefreshCw size={10} /> Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="px-2 pb-2">
        <div className="mx-auto max-w-6xl space-y-2">
          {!showForm ? (
            <div className={masterPanel}>
              <SearchBar 
                  className="p-2 border-b border-slate-200 flex-shrink-0 bg-white"
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }} 
                  placeholder="Search alphabetically by bill ID, patient, doctor, or phone..." 
                  totalItems={filteredBills.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />

              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px] text-left">
                  <thead>
                    <tr className="sticky top-0 z-10 border-b border-slate-400 bg-slate-100 text-left text-[9px] font-extrabold uppercase tracking-[0.14em] text-emerald-600">
                      <th className={masterTableData}>S.No</th>
                      <th className={masterTableData}>Bill ID</th>
                      <th className={masterTableData}>Patient</th>
                      <th className={masterTableData}>Doctor</th>
                      <th className={masterTableData}>Phone</th>
                      <th className={masterTableData}>Items</th>
                      <th className={masterTableData}>GST</th>
                      <th className={masterTableData}>Total</th>
                      <th className={masterTableData}>Payment</th>
                      <th className={masterTableData}>Date</th>
                      <th className={masterTableData}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-300 font-bold text-slate-700">
                    {paginatedBills.length > 0 ? paginatedBills.map((bill, index) => (
                      <tr key={bill._id} className="text-[11px] font-bold text-slate-800 hover:bg-emerald-50/30">
                        <td className={masterTableData}>
                          {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className={masterTableData}>{bill.bill_id}</td>
                        <td className={masterTableData} title={bill.patient_name}>{bill.patient_name}</td>
                        <td className={masterTableData}>{bill.doctor_name || '-'}</td>
                        <td className={masterTableData}>{bill.phone_number || '-'}</td>
                        <td className={masterTableData}>{bill.items?.length || 0}</td>
                        <td className={masterTableData}>{formatMoney(bill.gst_amount)}</td>
                        <td className={masterTableData}>{formatMoney(bill.total_amount)}</td>
                        <td className={masterTableData}>
                          <span className="rounded px-2 py-0.5 text-[9px] uppercase bg-slate-100">{bill.payment_mode || 'Cash'}</span>
                        </td>
                        <td className={masterTableData}>{formatDisplayDate(bill.createdAt || bill.billing_date || Date.now())}</td>
                        <td className={masterTableData}>
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => handleEdit(bill)} className="rounded-lg bg-slate-50 p-1.5 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                              <Edit size={14} />
                            </button>
                            <button onClick={() => handleDelete(bill)} className="rounded-lg bg-rose-50 p-1.5 text-rose-600 hover:bg-rose-600 hover:text-white">
                              <Trash2 size={14} />
                            </button>
                            <button onClick={() => printBillPdf(bill)} className="rounded-lg bg-slate-100 p-1.5 text-slate-700 hover:bg-slate-800 hover:text-white">
                              <Printer size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="11" className="p-8 text-center text-xs font-medium uppercase text-slate-300">No matching records found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-2`}>
              <div className="rounded-lg border border-slate-300 bg-slate-50 px-2 py-1">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                  {patientType === 'inside' ? (
                    <SearchSelect
                      label="Patient Search (Name / ID / Phone)"
                      value={patientSearch}
                      onChange={(value) => {
                        setPatientSearch(value.toUpperCase());
                        setSelectedPatientId('');
                        setPatientName(value.toUpperCase());
                      }}
                    onSelect={(option) => {
                        const targetId = option.value;
                        setSelectedPatientId(targetId);
                        setPatientSearch(option.label.toUpperCase());
                        setPatientName(option.patient?.fullName ? option.patient.fullName.toUpperCase() : option.label.toUpperCase());
                        setPhoneNumber(option.phone || '');

                        // 1. Get ALL prescriptions for this patient
                        const patientRx = prescriptions.filter(rx => String(rx.patient) === String(targetId));

                        if (patientRx.length > 0) {
                          // 2. Sort by date (newest first), THEN by ID (if dates are same, newer ID comes first)
                          patientRx.sort((a, b) => {
                            const dateCompare = new Date(b.visitDate) - new Date(a.visitDate);
                            if (dateCompare !== 0) return dateCompare;
                            // If dates match, sort by _id descending (assuming MongoDB ObjectID style)
                            return b._id.localeCompare(a._id);
                          });
                          
                          // 3. Take the absolute latest (index 0 after the robust sort)
                          const activeRx = patientRx[0];

                          // Set Doctor Name
                          if (activeRx?.doctor) {
                            const doctorInfo = doctorMap.get(String(activeRx.doctor));
                            const name = doctorInfo ? doctorInfo.doctorName : activeRx.doctor;
                            setDoctorName(name.toUpperCase());
                            setDoctorSearch(name.toUpperCase());
                          }

                          // 4. Look for Digital vs Handwritten among the most recent visit only
                          const latestDateStr = new Date(activeRx.visitDate).toDateString();
                          const todaysRecords = patientRx.filter(rx => new Date(rx.visitDate).toDateString() === latestDateStr);

                          const digitalRx = todaysRecords.find(rx => rx.type?.toLowerCase() === 'digital');
                          const handwrittenRx = todaysRecords.find(rx => rx.type?.toLowerCase() === 'handwritten' || (Array.isArray(rx.files) && rx.files.length > 0));

                          if (digitalRx && handwrittenRx) {
                            setPrescriptionSelectionModal({
                              medicines: digitalRx.medicines,
                              files: handwrittenRx.files
                            });
                          } else if (digitalRx) {
                            autofillMedicinesList(digitalRx.medicines);
                            setLinkedPrescription(digitalRx.prescriptionId || digitalRx._id);
                          } else if (handwrittenRx) {
                            openHandwrittenWindow(handwrittenRx.files);
                            setLinkedPrescription(handwrittenRx.prescriptionId || handwrittenRx._id);
                          }
                        } else {
                          setDoctorName('');
                          setDoctorSearch('');
                          setLinkedPrescription('');
                          setItems([createEmptyItem(0)]);
                        }
                      }}
                      options={patientOptions}
                      placeholder="Type Patient ID, Phone or Name..."
                      emptyText="No patient found"
                      forceOpenOnFocus
                      renderOption={(option) => (
                        <div className="flex flex-col text-left">
                          <span className="font-extrabold uppercase text-slate-800">
                            {option.patient?.fullName || option.label}
                          </span>
                          <span className="text-[9px] font-bold text-emerald-600">
                            ID: {option.patient?.patientId || 'N/A'} | Mob: {option.phone || 'N/A'}
                          </span>
                        </div>
                      )}
                    />
                  ) : (
                    <Input label="Patient Name" value={patientName} onChange={(e) => setPatientName(e.target.value.toUpperCase())} />
                  )}

                  <SearchSelect
                    label="Doctor Name"
                    value={doctorSearch || doctorName}
                    onChange={(value) => {
                      setDoctorSearch(value.toUpperCase());
                      setDoctorName(value.toUpperCase());
                    }}
                    onSelect={(option) => {
                      setDoctorSearch(option.label.toUpperCase());
                      setDoctorName(option.label.toUpperCase());
                    }}
                    options={patientType === 'inside' ? patientDoctorOptions : doctors
                      .map((doc) => ({
                        value: doc.doctorId || doc._id,
                        label: doc.doctorName || doc.doctorId || '',
                        search: [doc.doctorName, doc.doctorId].filter(Boolean).join(' ')
                      }))
                      .filter((item) => item.value && item.label)}
                    placeholder="Search or type doctor"
                    emptyText="Type doctor name manually"
                  />

                  <Input
                    label="Phone Number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(normalizePhoneInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    readOnly={patientType === 'inside'}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-slate-300 bg-slate-50 p-1">
                <div className="mb-1 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h2 className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-600">Medicine List</h2>
                    <label className="inline-flex items-center gap-1 text-[9px] font-bold uppercase text-slate-700">
                      <input
                        type="checkbox"
                        checked={useGenericFilter}
                        onChange={(e) => setUseGenericFilter(e.target.checked)}
                      />
                      Generic Name
                    </label>
                  </div>
                  <button type="button" onClick={addItem} className={masterAccentButton}>
                    <Plus size={14} /> Add Medicine
                  </button>
                </div>

                <div className="overflow-x-auto rounded-lg border border-slate-300 bg-white">
                  <div className="max-h-[350px] overflow-auto">
                    <table className="w-full min-w-[900px] table-fixed text-left text-[10px]">
                      <thead className="sticky top-0 border-b border-slate-300 bg-slate-100 text-[9px] font-extrabold uppercase tracking-[0.12em] text-emerald-600">
                        <tr>
                          <th className="w-[25%] p-0.5">Medicine Name</th>
                          <th className="w-[16%] p-0.5">Batch</th>
                          <th className="w-[10%] p-0.5 text-center">Expiry</th>
                          <th className="w-[8%] p-0.5 text-center">Avail</th>
                          <th className="w-[8%] p-0.5 text-center">Qty</th>
                          <th className="w-[9%] p-0.5 text-center">MRP</th>
                          <th className="w-[8%] p-0.5 text-center">Disc %</th>
                          <th className="w-[9%] p-0.5 text-center">Disc</th>
                          <th className="w-[7%] p-0.5 text-center">GST %</th>
                          <th className="w-[10%] p-0.5 text-center">Taxable</th> 
                          <th className="w-[10%] p-0.5 text-center">GST Amt</th>
                          <th className="w-[10%] p-0.5 text-center">Total</th>
                          <th className="w-[4%] p-0.5 text-right">X</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {items.map((item) => {
                          const itemMedicineOptions = getMedicineOptionsForItem(item);
                          const batchOptions = item.medicine_id
                            ? (stockByMedicine.get(String(item.medicine_id)) || [])
                                .filter((entry) => {
                                  const isCurrentSelection =
                                    String(entry.batch_number || '').trim().toUpperCase() ===
                                    String(item.batch_number || '').trim().toUpperCase();
                                  return Number(entry.quantity_available || 0) > 0 || isCurrentSelection;
                                })
                                .map((entry) => ({
                                  value: entry.batch_number,
                                  label: entry.batch_number,
                                  search: `${entry.batch_number} ${entry.expiry_date || ''}`,
                                  stock: entry.quantity_available,
                                  expiry_date: entry.expiry_date
                                }))
                            : [];
                          const batchKey = getBatchLookupKey(item.medicine_id, item.batch_number);
                          const batchInfo = item.batch_number
                            ? batchMap.get(batchKey) || grnBatchMap.get(batchKey)
                            : null;
                          const availableStock = batchInfo
                            ? Number(batchInfo.quantity_available || 0) + (editingId ? Number(editingReservedBatchStock.get(batchKey) || 0) : 0)
                            : 0;
                          const values = getLineValues(item);
                          const stockWarning = item.medicine_id && Number(item.qty || 0) > availableStock;

                          return (
                            <tr key={item.lineId} className="align-top text-slate-800">
                              <td className="p-0.5 align-top">
                                <div className={`grid gap-1 ${useGenericFilter ? 'grid-cols-[120px_minmax(0,1fr)]' : 'grid-cols-1'}`}>
                                  {useGenericFilter ? (
                                    <input
                                      type="text"
                                      value={item.generic_name || ''}
                                      onChange={(e) => {
                                        handleItemChange(item.lineId, 'generic_name', e.target.value.toUpperCase());
                                        handleItemChange(item.lineId, 'medicine_name', '');
                                        handleItemChange(item.lineId, 'medicine_id', '');
                                        handleItemChange(item.lineId, 'batch_number', '');
                                        handleItemChange(item.lineId, 'expiry_date', '');
                                        handleItemChange(item.lineId, 'price', 0);
                                      }}
                                      placeholder="Generic"
                                      className="w-full rounded border border-slate-300 px-1 py-1 text-[10px] font-bold uppercase outline-none focus:border-emerald-500"
                                    />
                                  ) : null}
                                  <SearchSelect
                                    label=""
                                    hideLabel
                                    value={item.medicine_name || ''}
                                    onChange={(value) => {
                                      handleItemChange(item.lineId, 'medicine_name', value.toUpperCase());
                                      handleItemChange(item.lineId, 'medicine_id', '');
                                      handleItemChange(item.lineId, 'batch_number', '');
                                      handleItemChange(item.lineId, 'expiry_date', '');
                                      handleItemChange(item.lineId, 'price', 0);
                                    }}
                                    onSelect={(option) => selectMedicineForLine(item.lineId, option.value)}
                                    options={itemMedicineOptions}
                                    placeholder={useGenericFilter ? 'Search medicine' : 'Search medicine'}
                                    emptyText="No medicine found"
                                    inputClassName="px-1.5 py-0.5 text-[10px]"
                                    renderOption={(option) => (
                                      <span className="font-bold uppercase">{option.label}</span>
                                    )}
                                  />
                                </div>
                              </td>
                              <td className="p-0.5">
                                <SearchSelect
                                  label=""
                                  hideLabel
                                  value={item.batch_number || ''}
                                  onChange={(value) => handleItemChange(item.lineId, 'batch_number', value.toUpperCase())}
                                  onSelect={(option) => selectBatchForLine(item.lineId, option.value)}
                                  options={batchOptions}
                                  placeholder="Search batch"
                                  emptyText="No batch found"
                                  inputClassName="px-1 py-0.5 text-[10px]"
                                  renderOption={(option) => (
                                    <div className="flex flex-col w-full border-b border-slate-300 last:border-b-0">
                                      <div className="flex items-center justify-between w-full leading-tight">
                                        <span className="font-extrabold uppercase text-slate-800 text-[10px]">
                                          {option.label}
                                        </span>
                                        <span className="text-[10px] font-black text-slate-500">
                                          {option.stock}
                                        </span>
                                      </div>
                                      <div className="leading-none pb-0.5">
                                        <span className="text-[9px] font-bold text-emerald-600">
                                          {option.expiry_date || '--/----'}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                />
                              </td>
                              <td className="p-0.5">
                                <div className="min-w-[62px] rounded border border-slate-200 bg-slate-50 px-1 py-1 text-center text-[9px] font-bold uppercase text-slate-700">
                                  {item.expiry_date ? formatDisplayDate(item.expiry_date) : '-'}
                                </div>
                              </td>
                              <td className="p-0.5">
                                <div className={`min-w-[42px] rounded border px-1 py-1 text-center text-[9px] font-bold ${availableStock > 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
                                  {availableStock}
                                </div>
                                {stockWarning ? <div className="mt-1 text-center text-[8px] font-bold uppercase text-rose-500">Low</div> : null}
                              </td>
                              <td className="p-0.5">
                                <input
                                  type="number"
                                  min="1"
                                  value={item.qty}
                                  onChange={(e) => handleItemChange(item.lineId, 'qty', e.target.value)}
                                  className={`${masterInput} px-1 py-0.5 text-center text-[10px]`}
                                />
                              </td>
                              <td className="p-0.5">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.price}
                                  onChange={(e) => handleItemChange(item.lineId, 'price', e.target.value)}
                                  className={`${masterInput} px-1 py-0.5 text-right text-[10px]`}
                                />
                              </td>
                              <td className="p-0.5">
                                <input
                                  type="number"
                                  min="0"
                                  value={item.discount_percent}
                                  onChange={(e) => handleLineDiscountPercentChange(item.lineId, e.target.value)}
                                  className={`${masterInput} px-1 py-0.5 text-right text-[10px]`}
                                />
                              </td>
                              <td className="p-0.5">
                                <input
                                  type="number"
                                  min="0"
                                  value={item.discount}
                                  readOnly 
                                  className={`${masterInput} px-1 py-0.5 text-right text-[10px] font-bold text-rose-600 bg-slate-50 cursor-not-allowed`}
                                />
                              </td>
                              <td className="p-0.5">
                                <input
                                  type="number"
                                  min="0"
                                  value={item.gst}
                                  onChange={(e) => handleItemChange(item.lineId, 'gst', e.target.value)}
                                  className={`${masterInput} px-1 py-0.5 text-right text-[10px] font-bold text-blue-600`}
                                />
                              </td>
                              <td className="p-0.5">
                                <div className="min-w-[65px] rounded border border-slate-300 bg-slate-50 px-1 py-0.5 text-right text-[10px] font-bold text-slate-900">
                                  {formatMoney(values.taxable)}
                                </div>
                              </td>
                              <td className="p-0.5">
                                <div className="min-w-[65px] rounded border border-slate-300 bg-slate-50 px-1 py-0.5 text-right text-[10px] font-bold text-slate-900">
                                  {formatMoney(values.gstAmount)}
                                </div>
                              </td>
                              <td className="p-0.5">
                                <div className="min-w-[70px] rounded border border-emerald-300 bg-emerald-50 px-1 py-0.5 text-right text-[10px] font-black text-emerald-700">
                                  {formatMoney(values.total)}
                                </div>
                              </td>
                              <td className="p-0.5 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() => removeItem(item.lineId)}
                                    className="inline-flex items-center justify-center rounded-lg p-0.5 text-rose-500 hover:bg-rose-100"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
                <div className="md:col-span-1 rounded-lg border border-slate-300 bg-slate-50 p-0.5">
                  <div className="grid grid-cols-2 gap-1">
                    <Input
                      label="Discount %"
                      type="number"
                      min="0"
                      value={billDiscountPercent}
                      onChange={(e) => {
                        const value = e.target.value;
                        setBillDiscountPercent(value);
                        const base = Math.max(totals.subtotal, 0);
                        setBillDiscountAmount(value === '' ? '' : ((base * Number(value || 0)) / 100).toFixed(2));
                      }}
                    />
                    <Input
                      label="Discount Amount"
                      type="number"
                      min="0"
                      value={billDiscountAmount}
                      readOnly
                    />
                  </div>
                </div>
                <div className="md:col-span-2 grid grid-cols-2 gap-1 md:grid-cols-4">
                  <ValueBox label="Subtotal" value={formatMoney(totals.subtotal)} />
                  <ValueBox label="Total Medicine Discount" value={formatMoney(totals.lineDiscount)} />
                  <ValueBox label="Total GST" value={formatMoney(totals.gstAmount)} />
                  <ValueBox label="Grand Total" value={formatMoney(totals.total)} />
                </div>
              </div>

              <div className="rounded-lg border border-slate-300 bg-slate-50 p-1">
                <div className="mb-0.5 flex flex-wrap items-center gap-1">
                  <div className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-600">
                    Payment Mode
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {PAYMENT_MODES.map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => togglePaymentMode(mode)}
                        className={`rounded-lg border px-1 py-0.5 text-[10px] font-bold uppercase ${paymentModes.includes(mode) ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 bg-white text-slate-700'}`}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-1 md:grid-cols-4">
                  {paymentModes.map((mode) => (
                    <Input
                      key={mode}
                      label={`${mode} Amount`}
                      type="number"
                      min="0"
                      value={paymentAmounts[mode]}
                      onChange={(e) => handlePaymentAmountChange(mode, e.target.value)}
                      readOnly={paymentModes.length > 1 && paymentModes[paymentModes.length - 1] === mode}
                    />
                  ))}
                </div>
              </div>

              <div className="text-right">
                <button
                  type="submit"
                  onClick={() => setSubmitAction('submit')}
                  disabled={isSubmitting}
                  className={`${masterAccentButton} justify-center px-1 py-0.5 disabled:opacity-70`}
                >
                  <Save size={16} /> {isSubmitting ? 'Saving...' : editingId ? 'Update Bill' : 'Submit Bill'}
                </button>
                <button
                  type="submit"
                  onClick={() => setSubmitAction('print')}
                  disabled={isSubmitting}
                  className={`${masterPrimaryButton} justify-center px-1 py-0.5 disabled:opacity-70`}
                >
                  <Printer size={16} /> {isSubmitting ? 'Saving...' : editingId ? 'Update & Print' : 'Submit & Print'}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      {/* PORTAL MODAL SELECTION OVERLAY */}
      {prescriptionSelectionModal && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl bg-white p-4 shadow-xl border border-slate-300">
            <h3 className="text-xs font-black uppercase text-slate-700 mb-1 tracking-wider text-center">
              Select Prescription Format
            </h3>
            <p className="text-[11px] font-bold text-slate-500 mb-3 text-center uppercase">
              This patient has both formats available for the latest record.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  autofillMedicinesList(prescriptionSelectionModal.medicines);
                  setPrescriptionSelectionModal(null);
                }}
                className="w-full py-2 bg-emerald-600 text-white rounded-lg text-[11px] font-extrabold uppercase hover:bg-emerald-700 transition tracking-wider"
              >
                Load Digital Medicines List
              </button>
              <button
                type="button"
                onClick={() => {
                  openHandwrittenWindow(prescriptionSelectionModal.files);
                  setPrescriptionSelectionModal(null);
                }}
                className="w-full py-2 bg-blue-600 text-white rounded-lg text-[11px] font-extrabold uppercase hover:bg-blue-700 transition tracking-wider"
              >
                View Handwritten Image / PDF
              </button>
              <button
                type="button"
                onClick={() => setPrescriptionSelectionModal(null)}
                className="w-full py-1.5 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-bold uppercase hover:bg-slate-200 transition text-center"
              >
                Cancel Selection
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const Input = ({ label, readOnly = false, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <input {...props} readOnly={readOnly} className={`${masterInput} ${readOnly ? 'bg-slate-100 text-slate-700' : ''}`} />
  </div>
);

const ValueBox = ({ label, value }) => (
  <div className={`${masterValueBox}`}>
    <div className={masterInputLabel}>{label}</div>
    <div className="text-base font-black text-slate-900">{value}</div>
  </div>
);

const SearchSelect = ({
  label,
  value,
  onChange,
  onSelect,
  options,
  placeholder,
  renderOption,
  hideLabel = false,
  emptyText = 'No data found',
  inputClassName = '',
  forceOpenOnFocus = false
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const inputWrapRef = useRef(null);
  const normalized = String(value || '').trim().toLowerCase();

  const filteredOptions = useMemo(() => {
    if (!normalized) return options.slice(0, 30);
    return options
      .filter((option) => {
        const searchToken = String(option.search || '').toLowerCase();
        const label = String(option.label || '').toLowerCase();
        const valueToken = String(option.value || '').toLowerCase();
        
        return (
          searchToken.includes(normalized) ||
          label.includes(normalized) ||
          valueToken.includes(normalized)
        );
      })
      .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')))
      .slice(0, 30);
  }, [normalized, options]);

  useLayoutEffect(() => {
    if (!open || !inputWrapRef.current) return;
    const updatePosition = () => {
      const rect = inputWrapRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownHeight = Math.min(224, Math.max(120, filteredOptions.length * 38 || 120));
      const showAbove = spaceBelow < 240 && rect.top > dropdownHeight;
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        top: showAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        maxHeight: dropdownHeight,
        zIndex: 9999
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, filteredOptions.length]);

  const dropdownContent = open ? (
    <div
      style={dropdownStyle}
      className="overflow-auto rounded-lg border border-slate-300 bg-white shadow-lg"
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option) => (
          <button
            key={`${option.value}-${option.label}`}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(option);
              setOpen(false);
            }}
            className="block w-full border-b border-slate-100 px-2 py-2 text-left text-[11px] hover:bg-emerald-50 last:border-b-0"
          >
            {renderOption ? renderOption(option) : <span className="font-bold uppercase text-slate-800">{option.label}</span>}
          </button>
        ))
      ) : (
        <div className="px-2 py-2 text-[10px] font-bold uppercase text-slate-400">{emptyText}</div>
      )}
    </div>
  ) : null;

  return (
    <div className="flex flex-col gap-1">
      {hideLabel ? null : <label className={masterInputLabel}>{label}</label>}
      <div ref={inputWrapRef} className="relative">
        <input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => {
            if (forceOpenOnFocus) setOpen(true);
          }}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          placeholder={placeholder}
          className={`${masterInput} ${inputClassName}`}
        />
      </div>
      {typeof document !== 'undefined' ? createPortal(dropdownContent, document.body) : null}
    </div>
  );
};

export default PharmacyBilling;